import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Flex,
  Heading,
  IconArrowOpenDownLine,
  IconArrowOpenUpLine,
  IconButton,
  Text,
  TextInput,
  View,
} from "@instructure/ui";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../../components/CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../../components/DialogYesNo/DialogYesNo";
import { ExternalService } from "../../../services/ExternalService";
import { Files } from "../../../services/FilesService";
import { TermsContext, UserDetailContext } from "../../../utils/Context";
import { InputDate } from "../../../components/InputDate";
import { printAgenda } from "./printAgendaUtils";
import AgendaItem from "./AgendaItem";
import PublishedAgendas from "./PublishedAgendas";
import GroupByToggle from "../GroupByToggle";
import {
  groupItemsBySection,
  groupItemsByGoalObjective,
} from "../utils/groupingUtils";
import { buildGoalTreeFromMeasures } from "../../../utils/buildGoalTreeFromMeasures";
import "./MeetingAgenda.print.css";

const createEmptyAgenda = (planId) => ({
  plan_id: planId,
  name: "",
  meeting_date: new Date().toISOString().split("T")[0],
  items: [],
  group_order: { mode: "section", order: [] },
  updated_at: new Date().toISOString(),
  updated_by: "",
});

/**
 * Build lookup maps from plan.sections for hydrating agenda items at render time.
 * Returns { assessmentMap, qualitativeMeasureMap }
 */
const buildPlanLookups = (plan) => {
  const assessmentMap = {};
  const qualitativeMeasureMap = {};

  if (!plan?.sections) return { assessmentMap, qualitativeMeasureMap };

  plan.sections.forEach((section) => {
    const sectionName = section.name || "";

    if (section.assessments) {
      section.assessments.forEach((assessment) => {
        const key = assessment.id;
        if (key) {
          assessmentMap[key] = { ...assessment, sectionName };
        }
      });
    }

    if (section.qualitative_measures) {
      section.qualitative_measures.forEach((measure) => {
        const key = measure.ddl_id;
        if (key) {
          qualitativeMeasureMap[key] = { ...measure, sectionName };
        }
      });
    }
  });

  return { assessmentMap, qualitativeMeasureMap };
};

/**
 * Resolve term statuses for an assessment using the same logic as DataCollectionStatus.
 * Returns array of { name, ended, isCurrent }
 */
const resolveTermStatuses = (assessment, insightsTerms, canvasTerms) => {
  const terms = assessment.terms || [];
  const termStatuses = [];

  terms.forEach((term) => {
    let resolvedEndAt = term.end_at || null;
    if (!resolvedEndAt && term.insights_term_id && insightsTerms) {
      const it = insightsTerms.find((i) => i.id === term.insights_term_id);
      if (it) resolvedEndAt = it.end_at;
    }

    const termCanvasTerms = term.canvas_terms || [];
    if (termCanvasTerms.length > 0) {
      termCanvasTerms.forEach((ct) => {
        const ctEndAt = ct.end_at || resolvedEndAt;
        const ended = ctEndAt ? new Date(ctEndAt) < new Date() : false;
        termStatuses.push({
          name: ct.name || term.name || "",
          ended,
          endAt: ctEndAt,
        });
      });
    } else {
      const ended = resolvedEndAt
        ? new Date(resolvedEndAt) < new Date()
        : false;
      termStatuses.push({ name: term.name || "", ended, endAt: resolvedEndAt });
    }
  });

  // For external assessments, resolve assessing_terms if no term statuses yet
  if (
    assessment.assessment_type === "external" &&
    assessment.assessing_terms &&
    assessment.assessing_terms.length > 0 &&
    termStatuses.length === 0
  ) {
    assessment.assessing_terms.forEach((termId) => {
      if (canvasTerms && canvasTerms.length > 0) {
        const ct = canvasTerms.find((c) => c.id === termId);
        if (ct) {
          const insightsTerm = insightsTerms
            ? insightsTerms.find((it) => it.id === ct.insights_term_id)
            : null;
          const name = insightsTerm ? insightsTerm.name : ct.name || termId;
          const endAt = insightsTerm ? insightsTerm.end_at : ct.end_at;
          const ended = endAt ? new Date(endAt) < new Date() : false;
          termStatuses.push({ name, ended, endAt });
          return;
        }
      }
      if (insightsTerms && insightsTerms.length > 0) {
        for (const it of insightsTerms) {
          const found = (it.canvas_terms || []).find(
            (ct) => ct.id === termId || ct.ddl_id === termId
          );
          if (found) {
            const ended = it.end_at ? new Date(it.end_at) < new Date() : false;
            termStatuses.push({
              name: it.name || termId,
              ended,
              endAt: it.end_at,
            });
            return;
          }
        }
      }
    });
  }

  // Determine which term is "current" (first non-ended term)
  let currentFound = false;
  return termStatuses.map((ts) => {
    if (!ts.ended && !currentFound) {
      currentFound = true;
      return { ...ts, isCurrent: true };
    }
    return { ...ts, isCurrent: false };
  });
};

const MeetingAgenda = ({
  plan,
  isExternal,
  agenda,
  setAgenda,
  groupBy,
  setGroupBy,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const { insightsTerms: contextInsightsTerms, canvasTerms } =
    useContext(TermsContext);
  const userDetails = useContext(UserDetailContext);

  const planId = plan?.ddl_id || plan?.id;
  const fileService = isExternal ? ExternalService : Files;

  const effectiveGroupBy = plan?.include_goals_objs ? groupBy : "section";

  // Reset group_order when groupBy mode changes
  const handleGroupByChange = (newValue) => {
    if (newValue !== groupBy) {
      setAgenda((prev) => ({
        ...prev,
        group_order: { mode: newValue, order: [] },
      }));
    }
    setGroupBy(newValue);
  };

  // Keep group_order.mode in sync with effectiveGroupBy (handles cross-tab toggle changes)
  useEffect(() => {
    if (agenda && agenda.group_order?.mode !== effectiveGroupBy) {
      setAgenda((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          group_order: {
            ...prev.group_order,
            mode: effectiveGroupBy,
            order: [],
          },
        };
      });
    }
  }, [effectiveGroupBy, agenda, setAgenda]);

  // For external access, fetch insights terms if context is empty
  const [externalInsightsTerms, setExternalInsightsTerms] = useState([]);
  useEffect(() => {
    if (
      (!contextInsightsTerms || contextInsightsTerms.length === 0) &&
      planId
    ) {
      const fetchTerms = async () => {
        try {
          const terms = await ExternalService.getInsightsTerms();
          setExternalInsightsTerms(terms || []);
        } catch {
          setExternalInsightsTerms([]);
        }
      };
      fetchTerms();
    }
  }, [contextInsightsTerms, planId]);

  const insightsTerms =
    contextInsightsTerms && contextInsightsTerms.length > 0
      ? contextInsightsTerms
      : externalInsightsTerms;

  const [publishedAgendas, setPublishedAgendas] = useState([]);
  const [loadingPublished, setLoadingPublished] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Build lookup maps from plan for hydration
  const { assessmentMap, qualitativeMeasureMap } = useMemo(
    () => buildPlanLookups(plan),
    [plan]
  );

  // Initialize empty agenda if none exists (local-only builder)
  useEffect(() => {
    if (!planId) return;
    if (agenda === null) {
      setAgenda(createEmptyAgenda(planId));
    }
  }, [planId, agenda, setAgenda]);

  // Fetch published agendas list via reviewFiles — metadata parsed from filename
  useEffect(() => {
    if (!planId) {
      setLoadingPublished(false);
      return;
    }
    let cancelled = false;

    const fetchPublished = async () => {
      try {
        const files = await fileService.reviewFiles("agenda", planId);
        if (!cancelled && Array.isArray(files)) {
          // Sort by last_modified descending (most recent first)
          const sorted = [...files].sort(
            (a, b) =>
              new Date(b.last_modified || 0) - new Date(a.last_modified || 0)
          );
          // Parse metadata from filename: {name}--{meeting_date}--{item_count}--{timestamp}.json
          const enriched = sorted.map((entry) => {
            const fname = (entry.file_name || "").replace(/\.json$/, "");
            const parts = fname.split("--");
            return {
              ...entry,
              agenda_name: parts.length >= 4 ? parts[0].replace(/_/g, " ") : "",
              agenda_meeting_date: parts.length >= 4 ? parts[1] : "",
              agenda_item_count:
                parts.length >= 4 ? parseInt(parts[2], 10) || 0 : 0,
            };
          });
          if (!cancelled) setPublishedAgendas(enriched);
        }
      } catch {
        if (!cancelled) setPublishedAgendas([]);
      } finally {
        if (!cancelled) setLoadingPublished(false);
      }
    };

    fetchPublished();
    return () => {
      cancelled = true;
    };
  }, [planId, isExternal]); // eslint-disable-line

  // Normalize agenda items to have camelCase fields for grouping utils
  const normalizedItems = useMemo(() => {
    const items = agenda?.items || [];
    return items.map((item) => ({
      ...item,
      sectionName: item.section_name || "",
      goalId: item.goal_id || null,
      objectiveId: item.objective_id || null,
    }));
  }, [agenda]);

  // Build goals tree for goal/objective grouping
  const goals = useMemo(() => {
    if (effectiveGroupBy !== "goalObjective") return [];
    const measures = normalizedItems
      .filter((item) => item.goalId)
      .map((item) => ({
        goal: {
          id: item.goalId,
          description: item.goal_description || item.goalId,
        },
        objective: item.objectiveId
          ? {
              id: item.objectiveId,
              description: item.objective_description || item.objectiveId,
            }
          : null,
      }));
    return buildGoalTreeFromMeasures(measures);
  }, [normalizedItems, effectiveGroupBy]);

  // Group agenda items based on effectiveGroupBy mode
  const groupedItems = useMemo(() => {
    if (normalizedItems.length === 0) return [];
    const sorted = [...normalizedItems].sort((a, b) => a.sequence - b.sequence);
    if (effectiveGroupBy === "goalObjective") {
      return groupItemsByGoalObjective(sorted, goals);
    }
    return groupItemsBySection(sorted);
  }, [normalizedItems, effectiveGroupBy, goals]);

  // Get current group keys from groupedItems for initializing custom order
  const currentGroupKeys = useMemo(() => {
    if (effectiveGroupBy === "section") {
      return groupedItems.map((g) => g.sectionName);
    }
    return groupedItems.map((g) => g.goalId);
  }, [groupedItems, effectiveGroupBy]);

  // Apply custom group order to rendered output
  const orderedGroups = useMemo(() => {
    const customOrder = agenda?.group_order?.order || [];
    if (customOrder.length === 0) return groupedItems;

    const getKey = (g) =>
      effectiveGroupBy === "section" ? g.sectionName : g.goalId;
    const orderMap = new Map(customOrder.map((key, idx) => [key, idx]));

    return [...groupedItems].sort((a, b) => {
      const aIdx = orderMap.get(getKey(a)) ?? Infinity;
      const bIdx = orderMap.get(getKey(b)) ?? Infinity;
      return aIdx - bIdx;
    });
  }, [groupedItems, agenda?.group_order?.order, effectiveGroupBy]);

  const handleMoveGroupUp = (groupKey) => {
    setAgenda((prev) => {
      const order = [
        ...(prev.group_order?.order?.length
          ? prev.group_order.order
          : currentGroupKeys),
      ];
      const idx = order.indexOf(groupKey);
      if (idx <= 0) return prev;
      [order[idx - 1], order[idx]] = [order[idx], order[idx - 1]];
      return { ...prev, group_order: { ...prev.group_order, order } };
    });
  };

  const handleMoveGroupDown = (groupKey) => {
    setAgenda((prev) => {
      const order = [
        ...(prev.group_order?.order?.length
          ? prev.group_order.order
          : currentGroupKeys),
      ];
      const idx = order.indexOf(groupKey);
      if (idx < 0 || idx >= order.length - 1) return prev;
      [order[idx], order[idx + 1]] = [order[idx + 1], order[idx]];
      return { ...prev, group_order: { ...prev.group_order, order } };
    });
  };

  // --- Event Handlers ---

  /**
   * Snapshot hydrated display data onto each agenda item at publish time.
   * This makes the published JSON a self-contained historical record,
   * immune to future plan changes or deletions.
   */
  const snapshotItems = (items) => {
    return items.map((item) => {
      const snapshot = { ...item };

      if (item.source_type === "assessment") {
        let assessment = assessmentMap[item.source_id];
        if (!assessment) {
          const parts = item.source_id.split("=");
          if (parts.length > 1) {
            assessment = assessmentMap[parts[1]] || assessmentMap[parts[0]];
          }
          if (!assessment) {
            const matchKey = Object.keys(assessmentMap).find(
              (key) =>
                key === item.source_id ||
                item.source_id.includes(key) ||
                key.includes(item.source_id)
            );
            if (matchKey) assessment = assessmentMap[matchKey];
          }
        }
        if (assessment) {
          const termStatuses = resolveTermStatuses(
            assessment,
            insightsTerms,
            canvasTerms
          );
          const totalTerms = termStatuses.length;
          const completedTerms = termStatuses.filter((ts) => ts.ended).length;
          snapshot.snapshot = {
            assessment_type: assessment.assessment_type || "",
            term_statuses: termStatuses.map((ts) => ({
              name: ts.name,
              ended: ts.ended,
              isCurrent: ts.isCurrent,
            })),
            total_terms: totalTerms,
            completed_terms: completedTerms,
            progress_percentage:
              totalTerms > 0
                ? Math.round((completedTerms / totalTerms) * 100)
                : 0,
          };
        }
      } else if (item.source_type === "qualitative_measure") {
        let measure = qualitativeMeasureMap[item.source_id];
        if (!measure) {
          const parts = item.source_id.split("=");
          if (parts.length > 1) {
            measure =
              qualitativeMeasureMap[parts[1]] ||
              qualitativeMeasureMap[parts[0]];
          }
          if (!measure) {
            const matchKey = Object.keys(qualitativeMeasureMap).find(
              (key) =>
                key === item.source_id ||
                item.source_id.includes(key) ||
                key.includes(item.source_id)
            );
            if (matchKey) measure = qualitativeMeasureMap[matchKey];
          }
        }
        if (measure) {
          let dueTermName = "";
          if (measure.insights_term_id && insightsTerms) {
            const term = insightsTerms.find(
              (it) => it.id === measure.insights_term_id
            );
            if (term) dueTermName = term.name;
          }
          snapshot.snapshot = {
            status: measure.status || "not_started",
            assignees: measure.assignees || "",
            due_term: dueTermName,
          };
        }
      }

      return snapshot;
    });
  };

  const handlePublish = async () => {
    if (!agenda || (agenda.items || []).length === 0) return;
    setSaving(true);
    try {
      const snapshotedItems = snapshotItems(agenda.items || []);
      const agendaToPublish = {
        ...agenda,
        items: snapshotedItems,
        updated_at: new Date().toISOString(),
        updated_by: userDetails?.email || "",
      };
      // Generate filename encoding metadata for list display (avoids N+1 fetches)
      // Format: {name}--{meeting_date}--{item_count}--{timestamp}.json
      const safeName = (agenda.name || "")
        .trim()
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .replace(/\s+/g, "_");
      const meetingDate = agenda.meeting_date || "";
      const itemCount = snapshotedItems.length;
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
      const filename = `${safeName}--${meetingDate}--${itemCount}--${timestamp}.json`;
      const blob = new File([JSON.stringify(agendaToPublish)], filename, {
        type: "application/json",
      });
      await fileService.saveFile(blob, "agenda", planId);
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.publishSuccess"),
        SUCCESS
      );
      // Clear local builder for next meeting
      setAgenda(createEmptyAgenda(planId));
      // Optimistically add the new entry to published list (avoids S3 eventual consistency delay)
      const optimisticEntry = {
        file_name: filename,
        last_modified: new Date().toISOString(),
        agenda_name: (agenda.name || "").trim(),
        agenda_meeting_date: meetingDate,
        agenda_item_count: itemCount,
      };
      setPublishedAgendas((prev) => [optimisticEntry, ...prev]);
    } catch {
      showNotification(
        t("assessmentPlanningDashboard.meetingAgenda.publishError"),
        ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setShowClearConfirm(true);
  };

  const confirmClear = () => {
    setAgenda((prev) => ({ ...prev, items: [] }));
    setShowClearConfirm(false);
  };

  const handleMeetingDateChange = (date) => {
    if (date) {
      const iso = date.toISOString().split("T")[0];
      setAgenda((prev) => ({ ...prev, meeting_date: iso }));
    }
  };

  const handleNameChange = (_e, value) => {
    setAgenda((prev) => ({ ...prev, name: value }));
  };

  const handlePrintInProgress = () => {
    if (!agenda || (agenda.items || []).length === 0) return;
    const snapshotedItems = snapshotItems(agenda.items || []);
    printAgenda({ ...agenda, items: snapshotedItems });
  };

  const items = agenda?.items || [];

  return (
    <View as="div" padding="small 0">
      {/* Group By Toggle */}
      {plan?.include_goals_objs && (
        <View as="div" margin="0 0 small 0">
          <GroupByToggle value={groupBy} onChange={handleGroupByChange} />
        </View>
      )}

      {/* Agenda Builder Header */}
      <View as="div" margin="0 0 medium 0">
        <Flex
          justifyItems="space-between"
          alignItems="end"
          wrap="wrap"
          gap="small"
        >
          <Flex.Item shouldGrow shouldShrink>
            <Flex gap="small" wrap="wrap" alignItems="end">
              <Flex.Item>
                <TextInput
                  renderLabel={
                    <>
                      {t("assessmentPlanningDashboard.meetingAgenda.nameLabel")}
                      <span style={{ color: "#d32f2f" }}> *</span>
                    </>
                  }
                  id="agenda-name"
                  value={agenda?.name || ""}
                  onChange={handleNameChange}
                  placeholder={t(
                    "assessmentPlanningDashboard.meetingAgenda.namePlaceholder"
                  )}
                  width="220px"
                />
              </Flex.Item>
              <Flex.Item>
                <InputDate
                  id="agenda-meeting-date"
                  label={t(
                    "assessmentPlanningDashboard.meetingAgenda.meetingDateLabel"
                  )}
                  selectedDate={agenda?.meeting_date || ""}
                  selectHandler={handleMeetingDateChange}
                />
              </Flex.Item>
            </Flex>
          </Flex.Item>
          <Flex.Item>
            <Flex gap="small" className="no-print">
              <Button
                size="small"
                color="primary"
                onClick={handlePublish}
                interaction={
                  items.length > 0 &&
                  !saving &&
                  (agenda?.name || "").trim().length > 0
                    ? "enabled"
                    : "disabled"
                }
              >
                {saving
                  ? t("global.saving")
                  : t("assessmentPlanningDashboard.meetingAgenda.publish")}
              </Button>
              <Button
                size="small"
                onClick={handlePrintInProgress}
                interaction={items.length > 0 ? "enabled" : "disabled"}
              >
                {t("assessmentPlanningDashboard.meetingAgenda.print")}
              </Button>
              <Button
                size="small"
                onClick={handleClear}
                interaction={items.length > 0 ? "enabled" : "disabled"}
              >
                {t("assessmentPlanningDashboard.meetingAgenda.clear")}
              </Button>
            </Flex>
          </Flex.Item>
        </Flex>
      </View>

      {/* Agenda Item List */}
      {items.length === 0 ? (
        <View as="div" padding="large" textAlign="center">
          <Text>
            {t("assessmentPlanningDashboard.meetingAgenda.emptyState")}
          </Text>
        </View>
      ) : (
        <View
          as="div"
          margin="0 0 medium 0"
          role="list"
          aria-label={t(
            "assessmentPlanningDashboard.meetingAgenda.agendaItemList"
          )}
        >
          <div aria-live="polite" aria-atomic="true" className="sr-only" />
          {effectiveGroupBy === "section"
            ? orderedGroups.map((group, groupIndex) => (
                <View key={group.sectionName} as="div" margin="0 0 medium 0">
                  <View as="div" margin="0 0 small 0">
                    <Flex alignItems="center" gap="small">
                      <Flex.Item shouldGrow>
                        <Text
                          weight="bold"
                          size="small"
                          color="secondary"
                          transform="uppercase"
                        >
                          {group.sectionName}
                        </Text>
                      </Flex.Item>
                      <Flex.Item>
                        <Flex gap="xx-small" className="no-print">
                          <IconButton
                            renderIcon={IconArrowOpenUpLine}
                            screenReaderLabel={t(
                              "assessmentPlanningDashboard.meetingAgenda.moveGroupUp",
                              { name: group.sectionName }
                            )}
                            size="small"
                            onClick={() => handleMoveGroupUp(group.sectionName)}
                            disabled={groupIndex === 0}
                          />
                          <IconButton
                            renderIcon={IconArrowOpenDownLine}
                            screenReaderLabel={t(
                              "assessmentPlanningDashboard.meetingAgenda.moveGroupDown",
                              { name: group.sectionName }
                            )}
                            size="small"
                            onClick={() =>
                              handleMoveGroupDown(group.sectionName)
                            }
                            disabled={groupIndex === orderedGroups.length - 1}
                          />
                        </Flex>
                      </Flex.Item>
                    </Flex>
                  </View>

                  {group.items.map((item, itemIdx) => (
                    <AgendaItem
                      key={item.item_id}
                      item={item}
                      indexInGroup={itemIdx}
                      groupItems={group.items}
                      assessmentMap={assessmentMap}
                      qualitativeMeasureMap={qualitativeMeasureMap}
                      insightsTerms={insightsTerms}
                      canvasTerms={canvasTerms}
                      setAgenda={setAgenda}
                      resolveTermStatuses={resolveTermStatuses}
                    />
                  ))}
                </View>
              ))
            : orderedGroups.map((goalGroup, groupIndex) => (
                <View key={goalGroup.goalId} as="div" margin="0 0 medium 0">
                  <View as="div" margin="0 0 small 0">
                    <Flex alignItems="center" gap="small">
                      <Flex.Item shouldGrow>
                        <Heading level="h4">
                          {goalGroup.goalDescription}
                        </Heading>
                      </Flex.Item>
                      <Flex.Item>
                        <Flex gap="xx-small" className="no-print">
                          <IconButton
                            renderIcon={IconArrowOpenUpLine}
                            screenReaderLabel={t(
                              "assessmentPlanningDashboard.meetingAgenda.moveGroupUp",
                              { name: goalGroup.goalDescription }
                            )}
                            size="small"
                            onClick={() => handleMoveGroupUp(goalGroup.goalId)}
                            disabled={groupIndex === 0}
                          />
                          <IconButton
                            renderIcon={IconArrowOpenDownLine}
                            screenReaderLabel={t(
                              "assessmentPlanningDashboard.meetingAgenda.moveGroupDown",
                              { name: goalGroup.goalDescription }
                            )}
                            size="small"
                            onClick={() =>
                              handleMoveGroupDown(goalGroup.goalId)
                            }
                            disabled={groupIndex === orderedGroups.length - 1}
                          />
                        </Flex>
                      </Flex.Item>
                    </Flex>
                  </View>
                  {goalGroup.objectiveGroups.map((objGroup) => (
                    <View
                      key={objGroup.objectiveId}
                      as="div"
                      margin="small 0 small medium"
                    >
                      <View as="div" margin="0 0 x-small 0">
                        <Text weight="bold" size="small" color="secondary">
                          {objGroup.objectiveDescription}
                        </Text>
                      </View>
                      {objGroup.items.map((item, itemIdx) => (
                        <AgendaItem
                          key={item.item_id}
                          item={item}
                          indexInGroup={itemIdx}
                          groupItems={objGroup.items}
                          assessmentMap={assessmentMap}
                          qualitativeMeasureMap={qualitativeMeasureMap}
                          insightsTerms={insightsTerms}
                          canvasTerms={canvasTerms}
                          setAgenda={setAgenda}
                          resolveTermStatuses={resolveTermStatuses}
                        />
                      ))}
                    </View>
                  ))}
                </View>
              ))}
        </View>
      )}

      {/* Published Agendas Section */}
      <PublishedAgendas
        publishedAgendas={publishedAgendas}
        loadingPublished={loadingPublished}
        planId={planId}
        fileService={fileService}
      />

      <DialogYesNo
        message={t(
          "assessmentPlanningDashboard.meetingAgenda.clearConfirmation"
        )}
        openStatus={showClearConfirm}
        onClickYes={confirmClear}
        onClickNo={() => setShowClearConfirm(false)}
      />
    </View>
  );
};

export default MeetingAgenda;
